// frontend/src/components/MiniHistoryGrid.jsx
import React, { useState, useEffect } from 'react';
import api from '../api';
import './MiniHistoryGrid.css';

const MiniHistoryGrid = ({ deviceId, maxColumns = 6 }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (deviceId) {
      fetchMiniHistoryData();
    }
  }, [deviceId]);

  // 获取设备的简化历史数据
  const fetchMiniHistoryData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/devices/${deviceId}`);
      const device = response.data;
      
      if (device && device.histogramBars) {
        // 只取最近的几轮数据
        const recentBars = device.histogramBars
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
          .slice(0, maxColumns);
        
        const transformedData = transformToMiniGrid(recentBars);
        setHistoryData(transformedData);
      } else {
        // 生成模拟数据
        setHistoryData(generateMockMiniData());
      }
    } catch (err) {
      console.error('获取设备迷你履历失败:', err);
      // 使用模拟数据作为后备
      setHistoryData(generateMockMiniData());
    } finally {
      setLoading(false);
    }
  };

  // 将设备数据转换为迷你格子图格式
  const transformToMiniGrid = (histogramBars) => {
    return histogramBars.map((bar, index) => {
      const totalTurns = bar.turnCount || 0;
      const consecutiveWins = bar.consecutivePrize || 0;
      
      // 每100转一个格子，最多显示4个格子
      const totalGrids = Math.ceil(totalTurns / 100);
      const maxGrids = Math.min(4, totalGrids);
      
      const grids = [];
      for (let gridIndex = 0; gridIndex < maxGrids; gridIndex++) {
        const turns = Math.min(100, Math.max(0, totalTurns - gridIndex * 100));
        const isActive = gridIndex < totalGrids && turns > 0;
        
        // 判断是否应该是红色格子（超过1000转）
        const shouldBeRed = totalTurns > 1000;
        const excessTurns = totalTurns - 1000;
        const redGridCount = shouldBeRed ? Math.ceil(excessTurns / 100) : 0;
        const isRedGrid = shouldBeRed && gridIndex < redGridCount;
        
        grids.push({
          id: `${index}-${gridIndex}`,
          turns: isActive ? turns : 0,
          isActive: isActive,
          isRed: isRedGrid
        });
      }
      
      return {
        id: index,
        totalTurns: totalTurns,
        consecutiveWins: consecutiveWins,
        grids: grids
      };
    });
  };

  // 生成模拟数据
  const generateMockMiniData = () => {
    const rounds = [];
    for (let i = 0; i < maxColumns; i++) {
      const totalTurns = Math.floor(Math.random() * 1200) + 300;
      const consecutiveWins = Math.floor(Math.random() * 4);
      
      const totalGrids = Math.ceil(totalTurns / 100);
      const maxGrids = Math.min(4, totalGrids);
      
      const grids = [];
      for (let gridIndex = 0; gridIndex < maxGrids; gridIndex++) {
        const turns = Math.min(100, Math.max(0, totalTurns - gridIndex * 100));
        const isActive = gridIndex < totalGrids && turns > 0;
        
        const shouldBeRed = totalTurns > 1000;
        const excessTurns = totalTurns - 1000;
        const redGridCount = shouldBeRed ? Math.ceil(excessTurns / 100) : 0;
        const isRedGrid = shouldBeRed && gridIndex < redGridCount;
        
        grids.push({
          id: `${i}-${gridIndex}`,
          turns: isActive ? turns : 0,
          isActive: isActive,
          isRed: isRedGrid
        });
      }
      
      rounds.push({
        id: i,
        totalTurns: totalTurns,
        consecutiveWins: consecutiveWins,
        grids: grids
      });
    }
    return rounds;
  };

  // 渲染单个迷你格子
  const renderMiniGrid = (grid) => {
    let className = 'mini-grid';
    
    if (grid.isActive) {
      if (grid.isRed) {
        className += ' active red';
      } else {
        className += ' active';
      }
    } else {
      className += ' inactive';
    }
    
    return (
      <div 
        key={grid.id} 
        className={className} 
        title={`${grid.turns}转`}
      />
    );
  };

  // 渲染单列
  const renderMiniColumn = (round) => {
    return (
      <div key={round.id} className="mini-column">
        <div className="mini-grids-column">
          {round.grids.map(grid => renderMiniGrid(grid))}
        </div>
        <div className="mini-turns">{round.totalTurns}</div>
        <div className="mini-wins">{round.consecutiveWins}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mini-history-container">
        <div className="mini-loading">...</div>
      </div>
    );
  }

  return (
    <div className="mini-history-container">
      <div className="mini-columns-container">
        {historyData.map(round => renderMiniColumn(round))}
      </div>
    </div>
  );
};

export default MiniHistoryGrid;
